import { useEffect, useState } from "react";
import { getPublicStats, type PublicStats } from "@/services/publicService";
import SiteButton from "@/components/site/SiteButton";
import PhotoFrame from "@/components/site/PhotoFrame";

interface HeroSectionProps {
  onRequest: () => void;
}

const count = (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString("fr-FR"));

export default function HeroSection({ onRequest }: HeroSectionProps) {
  // Chiffres réels de la plateforme (recalculés toutes les 10 minutes côté serveur).
  const [stats, setStats] = useState<PublicStats | null>(null);

  useEffect(() => {
    let alive = true;
    getPublicStats()
      .then((value) => alive && setStats(value))
      .catch(() => undefined); // Sans réseau : les tirets restent, la page reste utilisable.
    return () => {
      alive = false;
    };
  }, []);

  const figures = [
    { value: count(stats?.schools), label: stats?.schools === 1 ? "école sur la plateforme" : "écoles sur la plateforme" },
    { value: count(stats?.students), label: "élèves inscrits cette année" },
    { value: count(stats?.teachers), label: "enseignants" },
  ];

  return (
    <section className="relative overflow-hidden bg-background-100 pb-16 pt-28 md:pb-24 md:pt-36">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 md:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <div className="animate-slide-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-800">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
            Gestion scolaire, Guinée
          </span>
          <h1 className="mt-5 text-[2.5rem] font-extrabold font-heading leading-[1.05] tracking-tight text-primary-950 md:text-6xl">
            Toute la gestion de votre école, au même endroit.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-foreground-700">
            Inscriptions, frais de scolarité, notes, bulletins, caisse et paie du personnel. Du Primaire au
            Secondaire, chaque reçu et chaque bulletin sort en PDF, prêt à imprimer.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <SiteButton size="lg" onClick={onRequest} icon="ri-arrow-right-line">
              Demander un compte
            </SiteButton>
            <SiteButton size="lg" variant="outline" href="#fonctionnalites">
              Voir ce que fait l'application
            </SiteButton>
          </div>

          <dl className="mt-12 grid max-w-lg grid-cols-3 divide-x divide-background-300 border-t border-background-300 pt-6">
            {figures.map((f, index) => (
              <div key={f.label} className={index === 0 ? "pr-4" : "px-4"}>
                <dt className="sr-only">{f.label}</dt>
                <dd className="text-2xl font-bold font-heading tabular-nums text-primary-950 md:text-3xl">{f.value}</dd>
                <dd className="mt-1 text-xs leading-snug text-foreground-600 md:text-sm">{f.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative mx-auto w-full max-w-[560px] animate-scale-in">
          {/* Forme de fond décalée : le bleu marine du logo. */}
          <span
            className="absolute -right-3 -top-3 h-[88%] w-[88%] rounded-[2rem] bg-primary-950 md:-right-5 md:-top-5"
            aria-hidden="true"
          ></span>
          <PhotoFrame
            src="/images/accueil-classe.jpg"
            alt="Un enseignant accompagne des élèves en uniforme qui travaillent dans leurs cahiers"
            caption="Les notes saisies en classe font les bulletins du trimestre"
            captionPosition="top"
            eager
            className="relative aspect-[4/3] shadow-modal"
            imgClassName="object-[center_30%]"
          />
          <div className="absolute -bottom-7 -right-2 hidden rounded-2xl border border-background-200 bg-white px-4 py-3 shadow-raised sm:flex sm:items-center sm:gap-3 md:-right-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700" aria-hidden="true">
              <i className="ri-file-text-line text-xl"></i>
            </span>
            <span className="text-sm leading-tight">
              <span className="block font-semibold text-foreground-950">Reçu numéroté</span>
              <span className="text-foreground-600">à chaque paiement, en PDF</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
